import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowRight, ArrowDown, Banknote, ShieldCheck, Zap } from "lucide-react";
import { scrollToSection } from "../lib/scroll";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/a1675a88-61f5-4277-a147-25a19934fdab/images/e45626de0a0c5e994e75ec45eeb5d22264b6a03c4fffa373b1043d224a38d02f.jpeg";

const lineReveal = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const CHIPS = [
  { icon: Zap, label: "Fast processing" },
  { icon: Banknote, label: "Cash Deposit payment option" },
  { icon: ShieldCheck, label: "Secure order management" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(my, { stiffness: 120, damping: 16 });
  const rotateY = useSpring(mx, { stiffness: 120, damping: 16 });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 7);
    my.set(-((e.clientY - r.top) / r.height - 0.5) * 7);
  };
  const resetTilt = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section id="home" data-testid="hero-section" ref={ref} className="relative overflow-hidden pt-[72px]">
      <div className="bg-grid absolute inset-0" aria-hidden="true" />
      <div
        aria-hidden="true"
        className="absolute -top-32 left-1/2 h-[480px] w-[680px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[130px]"
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 right-0 h-[360px] w-[420px] rounded-full bg-cyan-500/[0.07] blur-[120px]"
      />

      <div className="container-x relative grid items-center gap-14 pb-20 pt-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:pb-28 lg:pt-24">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="eyebrow"
            data-testid="hero-eyebrow"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping-soft absolute inline-flex h-full w-full rounded-full bg-emerald-400" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            USDT to INR Exchange Platform
          </motion.div>

          <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-[4.4rem]">
            <span className="block overflow-hidden pb-1">
              <motion.span
                custom={0}
                variants={lineReveal}
                initial="hidden"
                animate="show"
                className="block"
              >
                Convert <span className="text-gradient">USDT</span>
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                custom={1}
                variants={lineReveal}
                initial="hidden"
                animate="show"
                className="block"
              >
                to <span className="text-gradient">INR</span> Easily
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 text-base leading-relaxed text-slate-400 sm:text-lg"
            data-testid="hero-subheadline"
          >
            Fast, simple and transparent USDT to INR exchange with convenient INR
            cash deposit payment options.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.72 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <Link
              data-testid="hero-primary-cta"
              to="/app"
              onClick={() =>
                sessionStorage.setItem("cdm-scroll", String(window.scrollY))
              }
              className="btn-primary px-7 py-3.5 text-base"
            >
              Login &amp; Create Order
              <ArrowRight className="h-5 w-5" />
            </Link>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToSection("how-it-works")}
              className="btn-outline px-7 py-3.5 text-base font-semibold"
            >
              How It Works
              <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap gap-2.5"
            data-testid="hero-trust-chips"
          >
            {CHIPS.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] px-4 py-2 text-xs font-semibold text-slate-300 backdrop-blur-sm sm:text-[13px]"
              >
                <Icon className="h-3.5 w-3.5 text-emerald-400" />
                {label}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          style={{ y: parallaxY }}
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
          data-testid="hero-visual"
          onMouseMove={onTilt}
          onMouseLeave={resetTilt}
        >
          <div
            aria-hidden="true"
            className="absolute inset-6 rounded-[2rem] bg-emerald-500/20 blur-[70px]"
          />
          <motion.div
            style={{ rotateX, rotateY, transformPerspective: 1000 }}
            className="relative rounded-3xl bg-gradient-to-br from-emerald-400/40 via-white/10 to-teal-400/30 p-[1.5px] shadow-glow"
          >
            <div className="overflow-hidden rounded-[calc(1.5rem-1px)] bg-navy-900">
              <img
                src={HERO_IMG}
                alt="USDT to INR exchange visual on CDM Pay"
                width={1264}
                height={848}
                className="h-auto w-full object-cover"
                loading="eager"
                fetchpriority="high"
              />
            </div>
          </motion.div>

          <div className="animate-float absolute -left-3 top-8 sm:-left-8">
            <div className="glass-card flex items-center gap-2.5 px-4 py-3 shadow-glow-sm">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-emerald-400/15">
                <ArrowRight className="h-4 w-4 rotate-[-45deg] text-emerald-300" />
              </span>
              <span className="font-mono text-xs font-semibold tracking-wider text-white">
                USDT <span className="text-emerald-400">→</span> INR
              </span>
            </div>
          </div>
          <div className="animate-float-slow absolute -right-3 bottom-10 sm:-right-6">
            <div className="glass-card flex items-center gap-2.5 px-4 py-3">
              <Banknote className="h-4 w-4 text-emerald-400" />
              <span className="text-xs font-semibold text-white">
                Cash Deposit Supported
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
